import React, { useRef, useCallback, useEffect } from 'react';
import { Search } from 'lucide-react';
import SearchResultsDropdown from './SearchResultsDropdown';
import LatestComments from './LatestComments';
/**
 * 
  Issue

  When typing "tron" in the search box, the real-time dropdown search results didn't show TRON coin, but clicking the search button worked correctly.

  Root Cause

  The search was using React state (searchAddress) which hadn't updated yet when the search function executed. Due to React's asynchronous state updates, when
  typing "tron", the search was actually executing with "tro" instead.

  Solution

  1. Added debouncing (300ms delay) to prevent searching on every keystroke
  2. Pass search term directly from the input's onChange event instead of relying on state
  3. Updated the chain of function calls to accept and forward the searchTerm parameter:
    - SearchBox.onChange → passes current input value to debouncedSearch(value)
    - debouncedSearch → passes value to onSearch(value)
    - handleSearch wrapper in App.js → passes to performSearch(searchTerm)
    - useUniversalSearch.handleSearch → uses the searchTerm parameter

  Now the search correctly searches for the complete term you've typed after you stop typing for 300ms, and TRON appears in the results when searching for "tron"!
 */
const SearchBox = ({
  searchAddress,
  setSearchAddress,
  searchResults,
  showSearchResults,
  setShowSearchResults,
  searchLoading,
  loading,
  onSearch
}) => {
  const debounceTimerRef = useRef(null);

  const debouncedSearch = useCallback((value) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      onSearch(value); // Pass the value directly
    }, 300); // 300ms debounce
  }, [onSearch]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <div className="bg-white border border-gray-300 rounded-lg p-4 shadow-sm">
      <LatestComments />
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 z-10" />
          <input
            type="text"
            value={searchAddress}
            onChange={(e) => {
              const value = e.target.value;
              setSearchAddress(value);
              if (value.trim().length >= 2) {
                debouncedSearch(value); // Pass the current value
              } else {
                setShowSearchResults(false);
              }
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                if (!showSearchResults || searchResults.totalResults === 0) {
                  onSearch();
                }
              }
            }}
            onFocus={() => {
              if (searchAddress.trim().length >= 2 && searchResults.totalResults > 0) {
                setShowSearchResults(true);
              }
            }}
            placeholder="Search addresses, coins, or tokens..."
            className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:border-blue-500"
          />
          <SearchResultsDropdown
            addresses={searchResults.addresses}
            coins={searchResults.coins}
            onClose={() => setShowSearchResults(false)}
            show={showSearchResults}
          />
        </div>
        <button
          onClick={() => onSearch()}
          disabled={searchLoading || loading}
          className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50 flex items-center gap-2"
        >
          {searchLoading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
          ) : (
            <Search className="w-5 h-5" />
          )}
          {searchLoading ? 'Searching...' : 'Search'}
        </button>
      </div>
    </div>
  );
};

export default SearchBox;
